/**
 * Comment Star Rating Component
 * Compact version of star rating for individual comments
 * Combines display and input in a smaller, inline format
 */

import { WidgetConfig } from '../types/index.js';
import './StarRating.css';

/**
 * Configuration for comment star rating component
 */
export interface CommentStarRatingConfig {
  /** Comment ID */
  commentId: string;
  /** Star rating statistics */
  stats: {
    average: number | null;
    count: number;
    distribution: Record<number, number>;
    userRating?: number | null;
  };
  /** Widget configuration */
  widgetConfig: WidgetConfig;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Callback when rating is submitted */
  onRatingSubmit: (commentId: string, rating: number) => Promise<void>;
  /** Callback when rating is cleared */
  onRatingClear: (commentId: string) => Promise<void>;
  /** Callback when login is required */
  onLoginRequired: () => void;
}

/**
 * CommentStarRating - Compact star rating component for comments
 */
export class CommentStarRating {
  private container: HTMLElement;
  private config: CommentStarRatingConfig;
  private hoverRating: number = 0;
  private submitting: boolean = false;

  /**
   * Creates a new CommentStarRating instance
   */
  constructor(config: CommentStarRatingConfig) {
    console.log('[CommentStarRating] Constructor called with config:', config);
    this.config = config;
    this.container = document.createElement('div');
    
    // Apply display mode class
    const displayMode = config.widgetConfig.commentStarRatingDisplayMode || 'compact';
    this.container.className = `komentas-comment-star-rating ${displayMode}`;
    
    console.log('[CommentStarRating] Container created with mode:', displayMode);
    this.render();
    console.log('[CommentStarRating] Render complete, container:', this.container);
  }

  /**
   * Gets the container element
   */
  getElement(): HTMLElement {
    return this.container;
  }

  /**
   * Updates the component with new data
   */
  update(stats: CommentStarRatingConfig['stats']): void {
    this.config.stats = stats;
    this.render();
  }

  /**
   * Updates authentication state
   */
  updateAuthState(isAuthenticated: boolean): void {
    this.config.isAuthenticated = isAuthenticated;
    this.render();
  }

  /**
   * Renders the component
   */
  private render(): void {
    console.log('[CommentStarRating] render() called');
    console.log('[CommentStarRating] Stats:', this.config.stats);
    console.log('[CommentStarRating] isAuthenticated:', this.config.isAuthenticated);
    console.log('[CommentStarRating] widgetConfig:', this.config.widgetConfig);
    
    this.container.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'comment-star-rating-wrapper';

    // Create stars container
    const starsContainer = document.createElement('div');
    starsContainer.className = 'comment-stars';

    // Determine if stars are interactive
    const hasUserRating = this.config.stats.userRating !== null && this.config.stats.userRating !== undefined;
    const canRate = this.config.isAuthenticated;
    const canUpdate = this.config.widgetConfig.allowCommentStarRatingUpdate !== false;
    const isInteractive = canRate && (!hasUserRating || canUpdate);

    console.log('[CommentStarRating] Interaction state:', {
      hasUserRating,
      canRate,
      canUpdate,
      isInteractive
    });

    if (!isInteractive) {
      starsContainer.classList.add('readonly');
    }

    // Create 5 stars
    for (let rating = 1; rating <= 5; rating++) {
      const star = this.createStar(rating, isInteractive);
      starsContainer.appendChild(star);
    }

    wrapper.appendChild(starsContainer);
    console.log('[CommentStarRating] Stars container created with', starsContainer.children.length, 'stars');

    // Add info text (average and count)
    if (this.config.stats.count > 0 || hasUserRating) {
      const info = document.createElement('span');
      info.className = 'comment-star-info';
      
      if (hasUserRating && this.config.stats.count === 0) {
        // Only user's rating
        info.textContent = `Your rating: ${this.config.stats.userRating} star${this.config.stats.userRating !== 1 ? 's' : ''}`;
      } else if (hasUserRating && this.config.stats.count > 0) {
        // User's rating + total
        const avg = this.config.stats.average?.toFixed(1) || '0.0';
        info.textContent = `${avg} (${this.config.stats.count}) · Your rating: ${this.config.stats.userRating}★`;
      } else if (this.config.stats.average !== null) {
        // Just average and count
        const avg = this.config.stats.average.toFixed(1);
        info.textContent = `${avg} (${this.config.stats.count})`;
      }
      
      wrapper.appendChild(info);
    }

    // Add "Login to rate" message if not authenticated
    if (!this.config.isAuthenticated && this.config.stats.count === 0) {
      const loginMsg = document.createElement('span');
      loginMsg.className = 'comment-star-login-msg';
      loginMsg.textContent = 'Login to rate';
      wrapper.appendChild(loginMsg);
    }

    this.container.appendChild(wrapper);
  }

  /**
   * Creates a star element
   */
  private createStar(rating: number, isInteractive: boolean): HTMLElement {
    const star = document.createElement('span');
    star.className = 'comment-star';
    star.setAttribute('data-rating', rating.toString());

    // Determine if star should be filled
    const userRating = this.config.stats.userRating || 0;
    const displayRating = this.hoverRating || userRating || this.config.stats.average || 0;
    
    if (rating <= Math.round(displayRating)) {
      star.classList.add('filled');
    }

    // Highlight user's rating
    if (userRating > 0 && rating <= userRating) {
      star.classList.add('user-rated');
    }

    if (isInteractive) {
      star.classList.add('interactive');

      // Mouse events for hover effect
      star.addEventListener('mouseenter', () => {
        if (!this.submitting) {
          this.hoverRating = rating;
          this.updateStarDisplay();
        }
      });

      star.addEventListener('mouseleave', () => {
        if (!this.submitting) {
          this.hoverRating = 0;
          this.updateStarDisplay();
        }
      });

      // Click event
      star.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await this.handleStarClick(rating);
      });

      // Make keyboard accessible
      star.setAttribute('role', 'button');
      star.setAttribute('tabindex', '0');
      star.setAttribute('aria-label', `Rate ${rating} star${rating !== 1 ? 's' : ''}`);

      star.addEventListener('keydown', async (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          await this.handleStarClick(rating);
        }
      });
    } else if (!this.config.isAuthenticated) {
      // Not authenticated - show login on click
      star.classList.add('login-required');
      star.style.cursor = 'pointer';
      
      star.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.config.onLoginRequired();
      });

      star.setAttribute('role', 'button');
      star.setAttribute('tabindex', '0');
      star.setAttribute('aria-label', 'Login to rate');
    }

    // Star icon
    star.innerHTML = rating <= Math.round(displayRating) ? '★' : '☆';

    return star;
  }

  /**
   * Handles star click
   */
  private async handleStarClick(rating: number): Promise<void> {
    if (this.submitting) {
      return;
    }

    const hasUserRating = this.config.stats.userRating !== null && this.config.stats.userRating !== undefined;

    // If clicking the same rating and updates are allowed, clear it
    if (hasUserRating && this.config.stats.userRating === rating) {
      const canUpdate = this.config.widgetConfig.allowCommentStarRatingUpdate !== false;
      
      if (canUpdate) {
        await this.clearRating();
      }
      return;
    }

    // Submit new rating
    await this.submitRating(rating);
  }

  /**
   * Submits a rating
   */
  private async submitRating(rating: number): Promise<void> {
    if (this.submitting) {
      return;
    }

    this.submitting = true;
    
    try {
      await this.config.onRatingSubmit(this.config.commentId, rating);
      // Update will be triggered by parent component
    } catch (error) {
      console.error('[CommentStarRating] Failed to submit rating:', error);
      alert('Failed to submit rating. Please try again.');
    } finally {
      this.submitting = false;
      this.hoverRating = 0;
    }
  }

  /**
   * Clears the rating
   */
  private async clearRating(): Promise<void> {
    if (this.submitting) {
      return;
    }

    this.submitting = true;
    
    try {
      await this.config.onRatingClear(this.config.commentId);
      // Update will be triggered by parent component
    } catch (error) {
      console.error('[CommentStarRating] Failed to clear rating:', error);
      alert('Failed to clear rating. Please try again.');
    } finally {
      this.submitting = false;
      this.hoverRating = 0;
    }
  }

  /**
   * Updates the star display based on hover state
   */
  private updateStarDisplay(): void {
    const stars = this.container.querySelectorAll('.comment-star');
    const userRating = this.config.stats.userRating || 0;
    const displayRating = this.hoverRating || userRating || this.config.stats.average || 0;

    stars.forEach((star, index) => {
      const rating = index + 1;
      const starElement = star as HTMLElement;
      
      // Update filled state
      if (rating <= Math.round(displayRating)) {
        starElement.classList.add('filled');
        starElement.innerHTML = '★';
      } else {
        starElement.classList.remove('filled');
        starElement.innerHTML = '☆';
      }

      // Keep user-rated class
      if (userRating > 0 && rating <= userRating && !this.hoverRating) {
        starElement.classList.add('user-rated');
      } else {
        starElement.classList.remove('user-rated');
      }
    });
  }

  /**
   * Destroys the component
   */
  destroy(): void {
    this.container.remove();
  }
}
